import { useMemo, useState } from "react";
import { Company, Group, NextVersion } from "../types/version_management_types";

/**
 * State for Version Management of Company & Groups
 *  - Maintains a nextCompanyVersion state which is undefined if not changed, and set to a NextVersion if it has.
 *  - Maintains a Record of NextVersion's keyed by group id for groups that have been changed. Value is null if group is to inherit the company version.
 *  - Combines the above state with the passed in props, so updates are automatically applied.
 */
export function useVersionManagementState(company: Company, groups: Group[]) {
    const [nextCompanyVersion, setNextCompanyVersion] = useState<undefined | NextVersion>(
        undefined
    );
    const [nextGroupVersions, setNextGroupVersions] = useState<
        Record<number, undefined | null | NextVersion>
    >({});

    const handleCompanyVersionChange = (nextVersion: undefined | NextVersion) => {
        setNextCompanyVersion(nextVersion);
    };

    const handleGroupVersionChange = (
        groupId: number,
        nextVersion: undefined | null | NextVersion
    ) => {
        setNextGroupVersions((prevState) => {
            return { ...prevState, [groupId]: nextVersion };
        });
    };

    const handleReset = () => {
        setNextCompanyVersion(undefined);
        setNextGroupVersions({});
    };

    const { companyState, hasCompanyChanges } = useMemo(() => {
        const state = {
            ...company,
            nextVersion: nextCompanyVersion,
        };

        const hasChanges =
            state.nextVersion !== undefined && state.currentTagId !== state.nextVersion?.tagId;

        return {
            companyState: state,
            hasCompanyChanges: hasChanges,
        };
    }, [company, nextCompanyVersion]);

    const { groupState, hasGroupChanges } = useMemo<{
        groupState: Group[];
        hasGroupChanges: boolean;
    }>(() => {
        const state = groups.map((group) => ({
            ...group,
            nextVersion: nextGroupVersions[group.id],
        }));

        const hasChanges = state.some((x) => {
            return x.nextVersion !== undefined && x.nextVersion?.tagId !== x.currentTagId;
        });

        return {
            groupState: state,
            hasGroupChanges: hasChanges,
        };
    }, [groups, nextGroupVersions]);

    return {
        companyState,
        groupState,
        setCompanyVersion: handleCompanyVersionChange,
        setGroupVersion: handleGroupVersionChange,
        reset: handleReset,
        hasCompanyChanges,
        hasGroupChanges,
        hasChanges: hasCompanyChanges || hasGroupChanges,
    };
}
